import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UseFormReturn,
  useFormContext,
  FieldPath,
  FieldValues,
} from 'react-hook-form';

import SSRIcon from '@ingka/ssr-icon';
import InformationIcon from '@ingka/ssr-icon/paths/information-circle';
import { useModal } from '@pcmp/react-providers';

import { IFormInputs } from '@/lib/schemas/create-code-form';

import ControlledInput from '@/components/form/controlled-input';
import ControlledTextArea from '@/components/form/controlled-textarea';
import ControlledSwitch from '@/components/form/controlled-switch';
import IconPreviewModal from '@/components/modals/icon-preview-modal';
import { CommunicationPurpose } from '@/typings/constants';

export type IFormContext = UseFormReturn<IFormInputs>;

export type CodeSettingsField<T extends FieldValues = IFormInputs> = {
  name: FieldPath<T>;
  label: string;
  type: 'number' | 'text' | 'password' | 'email' | 'hidden';
  ssrIcon?: (prefix?: string) => React.SVGProps<SVGElement>[];
  required?: boolean;
  paths?: string;
  onClick?: () => void;
  rows?: number;
  maxLength?: number;
  fieldType?: 'input' | 'textarea';
  className?: string;
};

type CodeSettingsProps<T extends FieldValues = IFormInputs> = {
  fields: CodeSettingsField<T>[];
  showIconSettings?: boolean;
  purposes: CommunicationPurpose[];
};

function CodeSettings<T extends FieldValues = IFormInputs>({
  fields,
  showIconSettings = false,
  purposes,
}: CodeSettingsProps<T>) {
  const { t } = useTranslation();
  const formContext = useFormContext();

  const {
    control,
    formState: { errors },
    watch,
    setValue,
    formState,
    getFieldState,
  } = formContext;

  const { openModal } = useModal();

  const openIconInfoModal = () => {
    openModal({
      component: IconPreviewModal,
      props: {
        confirm: () => {},
      },
    });
  };

  const showIcon = watch('showIcon');
  const currentCommunicationPurpose = watch('communicationPurpose');
  const currentSubcategory = watch('subcategory');

  const icons = useMemo(() => {
    if (!showIconSettings) return [];
    return (
      purposes
        .find((p) => p.value === currentCommunicationPurpose)
        ?.subcategories.find((subcat) => subcat.value === currentSubcategory)
        ?.icons || []
    );
  }, [
    showIconSettings,
    purposes,
    currentCommunicationPurpose,
    currentSubcategory,
  ]);

  const { isDirty: isSubcategoryDirty } = getFieldState(
    'subcategory',
    formState
  );

  const targetGroup = watch('targetGroup');
  const isTargetGroupCustomer = targetGroup === 'customer';

  useEffect(() => {
    if (showIconSettings && isSubcategoryDirty && isTargetGroupCustomer) {
      setValue('icon', icons[0]);
    }
  }, [
    showIconSettings,
    currentSubcategory,
    icons,
    isSubcategoryDirty,
    setValue,
    isTargetGroupCustomer,
  ]);

  const { isDirty: isShowIconDirty } = getFieldState('showIcon', formState);
  useEffect(() => {
    if (showIconSettings && isShowIconDirty) {
      if (showIcon && icons?.length > 0) {
        setValue('icon', icons[0]);
      } else {
        setValue('icon', '');
      }
    }
  }, [showIconSettings, showIcon, icons, isShowIconDirty, setValue]);

  return (
    <>
      <p className="leading-normal font-bold my-4">{t('label.codeSettings')}</p>
      <div className="grid lg:grid-cols-2 lg:gap-4">
        {fields.map((field) => (
          <div
            key={field.name}
            className={field.fieldType === 'textarea' ? 'lg:col-span-2' : ''}
          >
            {field.fieldType === 'textarea' ? (
              <ControlledTextArea
                form={formContext}
                name={field.name}
                label={field.label}
                shouldValidate={true}
                className="w-full"
                rows={field.rows}
                maxLength={field.maxLength}
              />
            ) : (
              <ControlledInput
                name={field.name}
                control={control}
                errors={errors}
                label={field.label}
                type={field.type}
                className="w-full"
                ssrIcon={field.ssrIcon}
              />
            )}
          </div>
        ))}
      </div>
      {showIconSettings && icons.length > 0 && (
        <ControlledSwitch
          name="showIcon"
          control={control}
          errors={errors}
          label={
            <>
              {t('label.showIcon')}
              <SSRIcon
                paths={InformationIcon}
                className="ms-2"
                onClick={(e) => {
                  e.preventDefault();
                  openIconInfoModal();
                }}
              />
            </>
          }
        />
      )}
    </>
  );
}

export default CodeSettings;
